// Newsletter players. Two players share one submit machinery:
//   • newsletter           → [data-newsletter-form]  → /subscribe    (brief 003)
//   • newsletterToolReport → [data-tool-report-form] → /tool-report  (brief 009)
// All copy arrives as server-rendered data-* attributes, so there is no browser i18n.

// The routes' own predicate, shared rather than reimplemented as a browser regex:
// being the server's function by construction, it can never reject an address the
// server would accept.
import { normalizeEmail } from 'newsletter-core/normalize.js';

// Registers the <altcha-widget> custom element (side-effect import). The widget
// solves an ALTCHA proof-of-work in the background (auto=onload) and the player
// reads its base64 solution off the `statechange` event to post as `altcha`.
import 'altcha';
import 'altcha/i18n/ru';
import 'altcha/i18n/de';
import 'altcha/i18n/fr-fr';
import 'altcha/i18n/es-es';

// Webmail deep-links: only for the domains we can send the user straight to.
const INBOX_URLS = {
  'gmail.com': 'https://mail.google.com',
  'mail.ru': 'https://e.mail.ru',
  'bk.ru': 'https://e.mail.ru',
  'inbox.ru': 'https://e.mail.ru',
  'list.ru': 'https://e.mail.ru',
  'yandex.ru': 'https://mail.yandex.ru',
  'ya.ru': 'https://mail.yandex.ru',
  'yahoo.com': 'https://mail.yahoo.com',
  'ymail.com': 'https://mail.yahoo.com',
  'outlook.com': 'https://outlook.live.com',
  'hotmail.com': 'https://outlook.live.com',
  'live.com': 'https://outlook.live.com'
};

function inboxUrlFor(email) {
  const domain = (email.split('@')[1] || '').toLowerCase();
  return INBOX_URLS[domain] || null;
}

// Shared submit machinery: client validation, the ALTCHA hold/auto-submit, the
// CSRF-safe POST, status rendering. Per-form differences arrive via opts —
// endpoint, submitSelector, collectExtras(form, el), renderSuccess(el, status,
// email, res, body).
const STATUS_BASE = 'newsletter-status mt-2';

function attachForm(el, opts) {
  const form = el.querySelector('form');
  const status = el.querySelector('.newsletter-status');
  if (!form || !status) {
    return;
  }
  const submitBtn = form.querySelector(opts.submitSelector);
  const widget = el.querySelector('altcha-widget');

  // Latest verified ALTCHA solution (base64 payload). Empty until the widget
  // verifies; cleared whenever it leaves the verified state.
  let altchaPayload = '';
  // True while a user-requested submit is held pending verification.
  let awaitingVerification = false;

  const showError = (message) => {
    status.className = `${STATUS_BASE} text-danger-emphasis`;
    status.textContent = message || '';
    status.focus();
  };

  // The widget exposes its solution on the statechange event; also mirror a
  // hidden `altcha` input in case verification finished before we listened.
  const readAltcha = () => {
    if (altchaPayload) {
      return altchaPayload;
    }
    const hidden = form.querySelector('input[name="altcha"]');
    return (hidden && hidden.value) ? hidden.value : '';
  };

  const submit = async () => {
    // Reset the status region before each attempt.
    status.textContent = '';
    status.className = STATUS_BASE;

    const emailInput = form.querySelector('input[name="email"]');
    const websiteInput = form.querySelector('input[name="website"]');
    const consentInput = form.querySelector('input[name="consent"]');
    const email = emailInput ? emailInput.value.trim() : '';

    const body = {
      email,
      website: websiteInput ? websiteInput.value : ''
    };
    if (consentInput) {
      body.consent = consentInput.checked;
    }
    if (opts.collectExtras) {
      Object.assign(body, opts.collectExtras(form, el));
    }

    // UX, not the security boundary — the server re-checks. normalizeEmail() also
    // covers the empty case, so one guard catches both. Return value discarded on
    // purpose: the POST sends the address as typed, the server canonicalizes.
    if (!normalizeEmail(email)) {
      showError(el.dataset.invalidEmail);
      return;
    }
    if (consentInput && !consentInput.checked) {
      showError(el.dataset.consentRequired);
      return;
    }

    // ALTCHA gate: if the background solve hasn't landed yet, hold the submit and
    // show "verifying" — the statechange listener re-invokes submit(). No widget at
    // all (altcha blocked) → POST anyway.
    const altcha = readAltcha();
    if (!altcha && widget) {
      awaitingVerification = true;
      if (submitBtn) {
        submitBtn.disabled = true;
      }
      status.className = `${STATUS_BASE} text-body`;
      status.textContent = el.dataset.verifying || '';
      return;
    }
    body.altcha = altcha;

    if (submitBtn) {
      submitBtn.disabled = true;
    }

    try {
      // apos.http.post attaches the CSRF X-XSRF-TOKEN header; a plain fetch()
      // would get a 403.
      const res = await apos.http.post(opts.endpoint, { body });
      opts.renderSuccess(el, status, email, res, body);
    } catch (err) {
      if (submitBtn) {
        submitBtn.disabled = false;
      }
      // The solution may now be spent server-side, so drop it and kick a fresh
      // solve — a retry with the spent payload would fake-succeed, sending nothing.
      altchaPayload = '';
      const hidden = form.querySelector('input[name="altcha"]');
      if (hidden) {
        hidden.value = '';
      }
      if (widget) {
        try {
          widget.reset();
          Promise.resolve(widget.verify()).catch(() => {});
        } catch (e) {
          // Widget unavailable — the server fails safe without a solution.
        }
      }
      // 4xx carries a distinguishable server message (invalid email / missing
      // consent). Anything else falls back to the generic error text.
      const clientError = err && err.status >= 400 && err.status < 500;
      const message = (clientError && err.message)
        ? err.message
        : (el.dataset.errorText || (err && err.message) || '');
      showError(message);
    }
  };

  if (widget) {
    widget.addEventListener('statechange', (event) => {
      const detail = event.detail || {};
      if (detail.state === 'verified') {
        altchaPayload = detail.payload || '';
        if (awaitingVerification) {
          awaitingVerification = false;
          submit();
        }
        return;
      }
      // Any non-verified state means no usable solution right now.
      altchaPayload = '';
      // Unstick a held submit if the widget failed/expired instead of
      // verifying, so the user isn't left on "verifying" forever.
      if (awaitingVerification && (detail.state === 'error' || detail.state === 'expired')) {
        awaitingVerification = false;
        if (submitBtn) {
          submitBtn.disabled = false;
        }
        showError(el.dataset.errorText);
      }
    });
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submit();
  });
}

// Replace the whole form section with a success screen and move focus to it.
// `extraLines` are optional paragraphs inserted between the detail and the
// check-spam hint (the tool-report forecast-confirmation line rides here).
function renderSuccessScreen(el, status, email, cfg) {
  const body = status.parentElement;

  // Drop everything in the body except the live region we render into.
  Array.from(body.children).forEach((child) => {
    if (child !== status) {
      child.remove();
    }
  });

  status.className = 'newsletter-status newsletter-success mt-2';
  status.textContent = '';

  const add = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    node.textContent = text;
    status.appendChild(node);
  };

  if (cfg.headline) {
    add('p', 'fw-bold mb-1', cfg.headline);
  }
  if (cfg.detail) {
    add('p', 'mb-1', cfg.detail);
  }
  (cfg.extraLines || []).forEach((line) => {
    if (line) {
      add('p', 'mb-1', line);
    }
  });
  if (cfg.checkSpam) {
    add('p', 'small text-body mb-1', cfg.checkSpam);
  }
  const inboxUrl = inboxUrlFor(email);
  if (inboxUrl && cfg.inboxLabel) {
    const a = document.createElement('a');
    a.className = 'btn btn-outline-primary btn-sm';
    a.href = inboxUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = cfg.inboxLabel;
    status.appendChild(a);
  }

  status.focus();
}

function renderSubscribeSuccess(el, status, email, res) {
  renderSuccessScreen(el, status, email, {
    headline: el.dataset.successText || (res && res.message) || '',
    detail: (el.dataset.successDetail || '').replace('{email}', email),
    checkSpam: el.dataset.checkSpam || '',
    inboxLabel: el.dataset.openInbox || ''
  });
}

function renderToolReportSuccess(el, status, email, res, body) {
  const extraLines = [];
  // The forecast-confirmation line only when the box was actually ticked.
  if (body && body.forecast && el.dataset.forecastFollowup) {
    extraLines.push(el.dataset.forecastFollowup);
  }
  renderSuccessScreen(el, status, email, {
    headline: el.dataset.successText || (res && res.message) || '',
    detail: (el.dataset.successDetail || '').replace('{email}', email),
    extraLines,
    checkSpam: el.dataset.checkSpam || '',
    inboxLabel: el.dataset.openInbox || ''
  });
}

export default () => {
  apos.util.widgetPlayers.newsletter = {
    selector: '[data-newsletter-form]',
    player(el) {
      attachForm(el, {
        endpoint: '/api/v1/@local/newsletter/subscribe',
        submitSelector: '[data-newsletter-submit]',
        renderSuccess: renderSubscribeSuccess
      });
    }
  };

  apos.util.widgetPlayers.newsletterToolReport = {
    selector: '[data-tool-report-form]',
    player(el) {
      attachForm(el, {
        endpoint: '/api/v1/@local/newsletter/tool-report',
        submitSelector: '[data-tool-report-submit]',
        collectExtras(form) {
          const toolInput = form.querySelector('input[name="tool"]');
          const forecastInput = form.querySelector('input[name="forecast"]');
          let params = {};
          try {
            params = JSON.parse(el.dataset.params || '{}');
          } catch (e) {
            params = {};
          }
          return {
            tool: toolInput ? toolInput.value : '',
            params,
            forecast: forecastInput ? forecastInput.checked : false
          };
        },
        renderSuccess: renderToolReportSuccess
      });
    }
  };
};
